import React, { useState } from 'react';
import { brand, getPrimaryButtonStyle, getSecondaryButtonStyle, getCardStyle } from '../../styles/brand';
import TrialCountdownBanner from '../onboarding/TrialCountdownBanner';

const { colors, gradients, typography, radius, shadows, spacing } = brand;

/**
 * QuickStartHome - Welcome screen for new users
 * P0 FIX: "Log Your First Meal" is now ABOVE MIQ explanation
 * Golden Path: Action before explanation
 */
const QuickStartHome = ({
  userName,
  userTier,
  isPremium,
  subscriptionDetails,
  IntelligenceLevelIndicator,
  onSetupProfile,
  onTakePhoto,
  onManualEntry,
  onBarcodeScanner,
  onShowUpgrade,
  onNavigateToMIQ,
  onSmartGoals,
  onSmartMeals,
  onSmartPicks,
  onUpgradeGenius
}) => {
  const [showMIQInfo, setShowMIQInfo] = useState(false);

  return (
    <div style={{ padding: spacing.s5, maxWidth: '500px', margin: '0 auto' }}>
      {/* Welcome Header */}
      <div style={{ textAlign: 'center', marginBottom: spacing.s5 }}>
        <h1 style={{ ...typography.h1, fontFamily: typography.fontFamily, color: colors.text, marginBottom: spacing.s2 }}>
          Welcome, {userName || 'there'}! 👋
        </h1>
        <p style={{ color: colors.textSecondary, fontSize: typography.body.fontSize }}>
          Log a meal to get started
        </p>
      </div>
      
      {/* Trial Countdown Banner — immediate visibility for new trial users */}
      <TrialCountdownBanner
        daysRemaining={subscriptionDetails?.trial_days_remaining}
        isTrial={subscriptionDetails?.is_trial || subscriptionDetails?.is_trialing}
        miqScore={null}
        totalMealsLogged={0}
        onUpgrade={onUpgradeGenius}
      />
      
      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* PRIMARY ACTION: Log Your First Meal - ABOVE THE FOLD */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div style={{
        background: `linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.08) 100%)`,
        border: `1px solid rgba(102, 126, 234, 0.25)`,
        borderRadius: radius.lg,
        padding: spacing.s6,
        textAlign: 'center',
        marginBottom: spacing.s4
      }}>
        <div style={{ fontSize: '48px', marginBottom: spacing.s3 }}>🍽️</div>
        <h2 style={{ ...typography.h2, fontFamily: typography.fontFamily, color: colors.text, marginBottom: spacing.s2 }}>
          Log Your First Meal
        </h2>
        <p style={{ fontSize: typography.body.fontSize, color: colors.textSecondary, marginBottom: spacing.s4 }}>
          Snap a photo of your food to get instant nutrition info
        </p>
        
        {/* PRIMARY CTA: Take Photo */}
        <button
          onClick={onTakePhoto}
          style={{ 
            width: '100%',
            padding: `${spacing.s4} ${spacing.s6}`, 
            fontSize: '17px',
            fontWeight: '600',
            background: gradients.primary,
            color: colors.text,
            border: 'none',
            borderRadius: radius.lg,
            cursor: 'pointer',
            boxShadow: shadows.glow,
            marginBottom: spacing.s3,
            minHeight: '56px',
            fontFamily: typography.fontFamily
          }}
        >
          📷 Take Photo
        </button>
        
        {/* Secondary: Manual Entry */}
        <button
          onClick={onManualEntry}
          style={{
            ...getSecondaryButtonStyle({ fullWidth: true }),
            background: 'transparent',
            border: `1px solid rgba(102, 126, 234, 0.3)`,
          }}
        >
          ✍️ Or describe it manually
        </button>
      </div>
      
      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* SECONDARY: Set Goal (after meal logging option) */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: spacing.s3, 
        marginBottom: spacing.s4,
        padding: `0 ${spacing.s5}`
      }}>
        <div style={{ flex: 1, height: '1px', background: colors.stroke }}></div>
        <span style={{ fontSize: typography.bodySmall.fontSize, color: colors.textSecondary }}>or set a goal first</span>
        <div style={{ flex: 1, height: '1px', background: colors.stroke }}></div>
      </div>
      
      <div style={{
        background: `linear-gradient(135deg, ${colors.accentGlow} 0%, rgba(118, 75, 162, 0.1) 100%)`,
        border: `1px solid rgba(102, 126, 234, 0.2)`,
        borderRadius: radius.lg,
        padding: spacing.s4,
        display: 'flex',
        alignItems: 'center',
        gap: spacing.s4,
        marginBottom: spacing.s5,
        cursor: 'pointer'
      }}
      onClick={onSetupProfile}
      >
        <div style={{ fontSize: '32px' }}>🎯</div>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: typography.body.fontSize, fontWeight: '600', margin: '0 0 4px 0', color: colors.text }}>Set Your First Goal</h3>
          <p style={{ fontSize: typography.bodySmall.fontSize, color: colors.textSecondary, margin: 0 }}>
            Weight target + timeline • Free
          </p>
        </div>
        <span style={{ color: colors.textSecondary, fontSize: '20px' }}>→</span>
      </div>
      
      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* SMART PLAITE - $4.99/mo (SmartGoals + Daily Check-ins) */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(56, 239, 125, 0.1) 0%, rgba(17, 153, 142, 0.08) 100%)',
        borderRadius: radius.lg,
        border: '1px solid rgba(56, 239, 125, 0.25)',
        padding: spacing.s4,
        marginBottom: spacing.s3
      }}>
        {/* Smart Plaite Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing.s2 }}>
          <div>
            <div style={{ fontSize: typography.body.fontSize, fontWeight: '700', color: colors.text, marginBottom: '2px' }}>
              🎯 Smart Plaite
            </div>
            <div style={{ fontSize: '11px', color: colors.textSecondary }}>
              Plan Your Nutrition
            </div>
          </div>
          <div style={{ 
            background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)', 
            color: colors.text, 
            padding: '4px 10px', 
            borderRadius: radius.sm,
            fontWeight: '700',
            fontSize: typography.bodySmall.fontSize,
            boxShadow: '0 2px 8px rgba(56, 239, 125, 0.3)'
          }}>$4.99/mo</div>
        </div>
        
        {/* Smart Plaite Features - 2x2 grid with 4 features */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr',
          gap: spacing.s2, 
          marginBottom: spacing.s3 
        }}>
          {/* SmartGoals */}
          <button
            onClick={onSmartGoals}
            style={{
              padding: `${spacing.s3} ${spacing.s2}`,
              background: 'linear-gradient(135deg, rgba(56, 239, 125, 0.15) 0%, rgba(56, 239, 125, 0.08) 100%)',
              border: '1px solid rgba(56, 239, 125, 0.35)',
              borderRadius: radius.md,
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.2s ease'
            }}
          >
            <span style={{ fontSize: '20px', display: 'block', marginBottom: '4px' }}>🎯</span>
            <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text, display: 'block' }}>SmartGoals</span>
            <span style={{ fontSize: '9px', color: colors.textSecondary, display: 'block' }}>AI-powered targets</span>
          </button>
          
          {/* Daily Check-ins */}
          <button
            onClick={onSmartGoals}
            style={{
              padding: `${spacing.s3} ${spacing.s2}`,
              background: 'linear-gradient(135deg, rgba(56, 239, 125, 0.15) 0%, rgba(56, 239, 125, 0.08) 100%)',
              border: '1px solid rgba(56, 239, 125, 0.35)',
              borderRadius: radius.md,
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.2s ease'
            }}
          >
            <span style={{ fontSize: '20px', display: 'block', marginBottom: '4px' }}>☀️</span>
            <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text, display: 'block' }}>Check-ins</span>
            <span style={{ fontSize: '9px', color: colors.textSecondary, display: 'block' }}>Sleep, steps, mood</span>
          </button>
          
          {/* Micronutrients */}
          <button
            onClick={onSmartGoals}
            style={{
              padding: `${spacing.s3} ${spacing.s2}`,
              background: 'linear-gradient(135deg, rgba(56, 239, 125, 0.15) 0%, rgba(56, 239, 125, 0.08) 100%)',
              border: '1px solid rgba(56, 239, 125, 0.35)',
              borderRadius: radius.md,
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.2s ease'
            }}
          >
            <span style={{ fontSize: '20px', display: 'block', marginBottom: '4px' }}>🧬</span>
            <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text, display: 'block' }}>Micronutrients</span>
            <span style={{ fontSize: '9px', color: colors.textSecondary, display: 'block' }}>Vitamins & minerals</span>
          </button>
          
          {/* Smart Timer */}
          <button
            onClick={onSmartGoals}
            style={{
              padding: `${spacing.s3} ${spacing.s2}`,
              background: 'linear-gradient(135deg, rgba(56, 239, 125, 0.15) 0%, rgba(56, 239, 125, 0.08) 100%)',
              border: '1px solid rgba(56, 239, 125, 0.35)',
              borderRadius: radius.md,
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.2s ease'
            }}
          >
            <span style={{ fontSize: '20px', display: 'block', marginBottom: '4px' }}>⏱️</span>
            <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text, display: 'block' }}>Smart Timer</span>
            <span style={{ fontSize: '9px', color: colors.textSecondary, display: 'block' }}>IF fasting tracker</span>
          </button>
        </div>
        
        <p style={{ fontSize: '10px', color: colors.textSecondary, margin: 0, textAlign: 'center' }}>
          + Barcode scanner • Menu search
        </p>
      </div>
      
      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* GENIUS PLAITE - $9.99/mo - YOUR PERSONAL METABOLIC INTELLIGENCE */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div style={{
        background: `linear-gradient(135deg, rgba(147, 51, 234, 0.12) 0%, ${colors.accentGlow} 100%)`,
        borderRadius: radius.lg,
        border: '1px solid rgba(147, 51, 234, 0.3)',
        padding: spacing.s4,
        marginBottom: spacing.s4
      }}>
        {/* Genius Plaite Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing.s2 }}>
          <div>
            <div style={{ fontSize: typography.body.fontSize, fontWeight: '700', color: colors.text, marginBottom: '2px' }}>
              🧠 Genius Plaite
            </div>
            <div style={{ fontSize: '11px', color: '#9333ea', fontWeight: '600' }}>
              Your AI Nutrition Coach
            </div>
          </div>
          <div style={{ 
            background: gradients.primary, 
            color: colors.text, 
            padding: '5px 12px', 
            borderRadius: radius.sm,
            fontWeight: '700',
            fontSize: typography.bodySmall.fontSize,
            boxShadow: shadows.glow
          }}>$9.99/mo</div>
        </div>
        
        {/* Compelling Value Proposition */}
        <div style={{
          background: 'rgba(147, 51, 234, 0.1)',
          borderRadius: radius.md,
          padding: spacing.s3,
          marginBottom: spacing.s4
        }}>
          <p style={{ margin: 0, fontSize: '11px', color: colors.textSecondary, lineHeight: '1.6' }}>
            Personalized guidance that adapts to your body and habits in real time. <span style={{ color: '#9333ea', fontWeight: '600' }}>Goes beyond tracking — tells you what to do next.</span>
          </p>
        </div>
        
        {/* Features Grid - 2x2 */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr',
          gap: spacing.s2,
          marginBottom: spacing.s3
        }}>
          {/* SmartMeals */}
          <button
            onClick={onSmartMeals}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.s3,
              padding: spacing.s3,
              background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.15) 0%, rgba(147, 51, 234, 0.08) 100%)',
              border: '1px solid rgba(147, 51, 234, 0.35)',
              borderRadius: radius.md,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s ease'
            }}
          >
            <span style={{ fontSize: '20px' }}>🍽️</span>
            <div>
              <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text, display: 'block' }}>SmartMeals</span>
              <span style={{ fontSize: '9px', color: colors.textSecondary }}>AI meal planning</span>
            </div>
          </button>
          
          {/* Smart Swaps */}
          <button
            onClick={onSmartPicks}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.s3,
              padding: spacing.s3,
              background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.15) 0%, rgba(147, 51, 234, 0.08) 100%)',
              border: '1px solid rgba(147, 51, 234, 0.35)',
              borderRadius: radius.md,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s ease'
            }}
          >
            <span style={{ fontSize: '20px' }}>🍴</span>
            <div>
              <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text, display: 'block' }}>Smart Swaps</span>
              <span style={{ fontSize: '9px', color: colors.textSecondary }}>Healthy recipes</span>
            </div>
          </button>
          
          {/* Weekly Intelligence Brief */}
          <button
            onClick={onNavigateToMIQ}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.s3,
              padding: spacing.s3,
              background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.15) 0%, rgba(147, 51, 234, 0.08) 100%)',
              border: '1px solid rgba(147, 51, 234, 0.35)',
              borderRadius: radius.md,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s ease'
            }}
          >
            <span style={{ fontSize: '20px' }}>📊</span>
            <div>
              <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text, display: 'block' }}>Weekly Brief</span>
              <span style={{ fontSize: '9px', color: colors.textSecondary }}>Intelligence reports</span>
            </div>
          </button>
          
          {/* MIQ Profile */}
          <button
            onClick={onNavigateToMIQ}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.s3,
              padding: spacing.s3,
              background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.15) 0%, rgba(147, 51, 234, 0.08) 100%)',
              border: '1px solid rgba(147, 51, 234, 0.35)',
              borderRadius: radius.md,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s ease'
            }}
          >
            <span style={{ fontSize: '20px' }}>🧠</span>
            <div>
              <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text, display: 'block' }}>MIQ Profile</span>
              <span style={{ fontSize: '9px', color: colors.textSecondary }}>Your baseline</span>
            </div>
          </button>
        </div>
        
        {/* Additional Benefits */}
        <div style={{ 
          paddingTop: spacing.s3,
          borderTop: '1px solid rgba(147, 51, 234, 0.2)'
        }}>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: spacing.s2, 
            justifyContent: 'center',
            fontSize: '10px',
            color: colors.textSecondary
          }}>
            <span>🎯 SmartGoals</span>
            <span>•</span>
            <span>☀️ Daily Check-ins</span>
            <span>•</span>
            <span>🧠 MIQ</span>
          </div>
          <p style={{ 
            margin: `${spacing.s3} 0 0 0`, 
            textAlign: 'center', 
            fontSize: '10px',
            color: colors.success,
            fontWeight: '600'
          }}>
            ✓ + Everything in Smart Plaite
          </p>
        </div>
      </div>
      
      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* MIQ EXPLANATION - Collapsed by default, below the fold */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div style={{
        background: colors.accentGlow,
        borderRadius: radius.lg,
        border: `1px solid rgba(102, 126, 234, 0.15)`,
        overflow: 'hidden'
      }}>
        {/* Collapsed Header - Always visible */}
        <button
          onClick={() => setShowMIQInfo(!showMIQInfo)}
          style={{
            width: '100%',
            padding: `${spacing.s4} ${spacing.s4}`,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.s3 }}>
            <span style={{ fontSize: '20px' }}>🧠</span>
            <span style={{ fontSize: typography.body.fontSize, fontWeight: '500', color: colors.text }}>
              What is MIQ?
            </span>
          </div>
          <span style={{ 
            color: colors.textSecondary, 
            fontSize: typography.body.fontSize,
            transform: showMIQInfo ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        </button>
        
        {/* Expandable Content */}
        {showMIQInfo && (
          <div style={{ padding: `0 ${spacing.s4} ${spacing.s4} ${spacing.s4}` }}>
            <p style={{ margin: `0 0 ${spacing.s3} 0`, fontSize: typography.bodySmall.fontSize, color: colors.text, lineHeight: '1.6' }}>
              <strong>MIQ (Personal MetaBolicIQ Intelligence Profile)</strong> is your evolving personal health assistant. It learns from every meal you log 
              and personalizes recommendations to your body.
            </p>
            
            <div style={{ 
              background: 'rgba(0,0,0,0.15)', 
              borderRadius: radius.md, 
              padding: spacing.s3,
              marginBottom: spacing.s3
            }}>
              <p style={{ margin: `0 0 ${spacing.s2} 0`, fontSize: '11px', fontWeight: '600', color: colors.accent }}>
                How it evolves:
              </p>
              <ul style={{ margin: 0, padding: '0 0 0 14px', fontSize: '11px', color: colors.textSecondary, lineHeight: '1.7' }}>
                <li><strong>Day 1-7:</strong> Learning your eating patterns</li>
                <li><strong>Day 8-14:</strong> Building your metabolic baseline</li>
                <li><strong>Day 15-30:</strong> Personalizing recommendations</li>
                <li><strong>Day 30+:</strong> Continuously adapting</li>
              </ul>
            </div>
            
            <p style={{ margin: 0, fontSize: '10px', color: colors.textSecondary, fontStyle: 'italic' }}>
              Your data is used only to personalize your experience — never shared externally.
            </p>
          </div>
        )}
      </div>
      
      {/* Premium Badge - Trial info */}
      {subscriptionDetails?.is_trial && (
        <div style={{
          marginTop: spacing.s4,
          padding: `${spacing.s3} ${spacing.s4}`,
          background: `linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 152, 0, 0.15) 100%)`,
          borderRadius: radius.md,
          border: `1px solid ${colors.warning}30`,
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, fontSize: typography.bodySmall.fontSize, color: colors.text }}>
            🎁 <strong>{subscriptionDetails?.trial_days_remaining || 7}-Day Free Trial Active</strong> • Enjoy AI meal planning, Smart Coach & more
          </p>
        </div>
      )}
      
      {/* Footer Disclaimer Link */}
      <p style={{ 
        textAlign: 'center', 
        marginTop: spacing.s5, 
        fontSize: '11px', 
        color: colors.textSecondary 
      }}>
        <span 
          onClick={onNavigateToMIQ} 
          style={{ textDecoration: 'underline', cursor: 'pointer' }}
        >
          Legal & Medical Disclaimer
        </span>
      </p>
    </div>
  );
};

export default QuickStartHome;
